import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API_BASE } from "../../lib/api";
import CaptureAnalysis from "../../components/capture/CaptureAnalysis";
import ViewSwitcher from "../../components/capture/ViewSwitcher";
import { ArrowLeft } from "lucide-react";

/**
 * A student's view of one of their own analyses.
 *
 * Reuses the same read-only renderer as the public share link, so what the
 * athlete sees in the app and what they see from a shared URL can't drift apart.
 * The renderer deliberately ignores `coach_rating` — that's the coach's private
 * calibration, not part of the coaching output.
 */
export default function StudentCaptureDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/capture/sessions/${id}`)
      .then(({ data }) => {
        if (alive) {
          setDoc(data);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (alive) {
          setError(e?.response?.status === 404 ? "This analysis is no longer available." : "Could not load this analysis.");
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return <div className="text-sm text-[#A3A3A3]">Loading…</div>;
  }

  if (error || !doc) {
    return (
      <div className="card-surface p-10 text-center max-w-xl mx-auto" data-testid="student-capture-error">
        <div className="font-display text-xl font-black uppercase tracking-tight">Not available</div>
        <p className="text-sm text-[#A3A3A3] mt-2">{error}</p>
        <Link
          to="/student/captures"
          className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2 mt-6"
        >
          <ArrowLeft size={12} /> All analyses
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-capture-detail-page">
      <Link
        to="/student/captures"
        className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold inline-flex items-center gap-1"
        data-testid="back-to-captures"
      >
        <ArrowLeft size={11} /> All analyses
      </Link>

      {/* Two camera angles on this analysis render as one page with a switcher,
          rather than two unrelated cards in the list. */}
      <ViewSwitcher
        views={doc.views || []}
        linkFor={(id) => `/student/captures/${id}`}
        testIdPrefix="student-view"
      />

      <CaptureAnalysis
        analysis={doc}
        // API_BASE is relative ("/api"), so this stays same-origin and keeps the
        // auth cookie — which the clip route requires.
        clipSrc={doc.clip_filename ? `${API_BASE}/capture/sessions/${doc.id}/clip` : null}
        clipDownloadName={`pbcoach-analysis-${doc.id}.mp4`}
        footnote={doc.coach_name ? `Analysis from ${doc.coach_name}.` : null}
      />
    </div>
  );
}
